import * as React from 'react';
import { Route, Routes } from 'react-router-dom';

/**
 * Roteamento gerado a partir de src/pages/, replicando a convencao de arquivos
 * do Next para nao ser necessario declarar 39 rotas a mao.
 *
 * Regras de conversao:
 *   pages/index.js                        -> /
 *   pages/dashboard/app/index.tsx         -> /dashboard/app
 *   pages/dashboard/politicas/new/[name]  -> /dashboard/politicas/new/:name
 *   pages/404.tsx                         -> * (catch-all)
 *
 * GANHO IMEDIATO: pastas "components/" dentro de pages/ sao EXCLUIDAS. No Next
 * elas viravam rotas publicas reais - o build listava 31 delas, por exemplo
 * /dashboard/users/components/list/InvoiceTableRow, que respondia 200 e
 * renderizava um componente sem props. Aqui elas simplesmente nao entram.
 *
 * `getLayout` continua sendo respeitado: as paginas do template definem
 * Page.getLayout para escolher entre o layout main e o do dashboard.
 */

type ModuloPagina = {
  default: React.ComponentType<any> & { getLayout?: (pagina: React.ReactElement) => React.ReactNode };
};

// IMPORTANTE: as exclusoes precisam estar no PROPRIO glob, nao apenas no filtro
// abaixo. Com `eager: true` todo arquivo casado e importado de fato - deixar
// _app/_document no padrao arrastava `next/app` e `next/document` para dentro do
// bundle do Vite, e as pastas components/ (31 arquivos) eram empacotadas sem
// necessidade.
const modulos = import.meta.glob<ModuloPagina>(
  [
    './pages/**/*.{tsx,jsx,js}',
    '!./pages/_app.{tsx,jsx,js}',
    '!./pages/_document.{tsx,jsx,js}',
    '!./pages/**/components/**',
  ],
  { eager: true }
);

function caminhoParaRota(arquivo: string): string | null {
  let rota = arquivo
    .replace('./pages', '')
    .replace(/\.(tsx|jsx|js)$/, '');

  // Arquivos especiais do Next e componentes que nao sao paginas.
  if (/(^|\/)_app$|(^|\/)_document$/.test(rota)) return null;
  if (/\/components\//.test(rota)) return null;
  if (/\.old$/.test(rota)) return null;

  // index -> diretorio pai
  rota = rota.replace(/\/index$/, '');
  if (rota === '') rota = '/';

  // [param] -> :param   e   [...resto] -> *
  rota = rota.replace(/\[\.\.\.(.+?)\]/g, '*').replace(/\[(.+?)\]/g, ':$1');

  return rota;
}

export default function AppRoutes() {
  const rotas = React.useMemo(() => {
    const lista: Array<{ rota: string; elemento: React.ReactNode }> = [];
    let paginaNaoEncontrada: React.ReactNode = null;

    Object.entries(modulos).forEach(([arquivo, modulo]) => {
      const rota = caminhoParaRota(arquivo);
      if (!rota) return;

      const Componente = modulo?.default;
      if (!Componente) return;

      const aplicarLayout = Componente.getLayout ?? ((pagina: React.ReactElement) => pagina);
      const elemento = aplicarLayout(<Componente />);

      if (rota === '/404') {
        paginaNaoEncontrada = elemento;
        return;
      }

      lista.push({ rota, elemento });
    });

    // Rotas mais especificas primeiro, para ":param" nao capturar antes de um
    // caminho literal do mesmo nivel.
    lista.sort((a, b) => {
      const paramsA = (a.rota.match(/:/g) || []).length;
      const paramsB = (b.rota.match(/:/g) || []).length;
      if (paramsA !== paramsB) return paramsA - paramsB;
      return b.rota.length - a.rota.length;
    });

    return { lista, paginaNaoEncontrada };
  }, []);

  return (
    <Routes>
      {rotas.lista.map(({ rota, elemento }) => (
        <Route key={rota} path={rota} element={elemento} />
      ))}
      {rotas.paginaNaoEncontrada && <Route path="*" element={rotas.paginaNaoEncontrada} />}
    </Routes>
  );
}
